import { SCHEMA } from '@vedalib/editor/lib/brand'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { FieldColorPicker, FieldSegmented, FieldSlider, FormField } from 'components/formKit'
import { Typography } from 'components/uiKit/Typography'
import { BrandButtonFont, BrandFieldShadow } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useElementFontCss, useElementFontValues, useUpdateSchema } from 'services/Branding/hooks'
import { t } from 'services/Translation'

import type { TestInfoFormBrandType } from './testInfoTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.testInfoEnd.style, {
  circle: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.test-info.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const TestInfoFormStyle: TestInfoFormBrandType = ({
  device,
  subConfigs,
  edited,
  element,
  setForm,
}) => {
  const { font, type = 'testInfoStart' } = element || {}
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)
  const values = useElementFontValues(type as 'testInfoEnd' | 'testInfoStart', device, font)
  const styles = useElementFontCss(type as 'testInfoEnd' | 'testInfoStart', device, font)

  const handleSetTypography = useCallback(() => setForm?.('base'), [setForm])

  return (
    <Padding bottom gap sides>
      <Typography weight='medium'>{t('elements.test-info.tags.icon')}</Typography>
      <FormField config={schema.circle.backgroundColor} />
      <FormField config={schema.circle.color} />
      <FormField config={schema.circle.borderStyle} />
      <FormField config={schema.circle.borderColor} />
      <FormField config={schema.circle.borderWidth} />

      <Typography weight='medium'>{t('style.effects')}</Typography>
      <FormField config={schema.effects.boxShadow} />

      <BrandButtonFont
        edited={edited?.font?.base}
        font='base'
        onClick={handleSetTypography}
        styles={styles.base}
        values={values.base}
      >
        {t('page.brand.tab.typography')}
      </BrandButtonFont>
    </Padding>
  )
}

export default React.memo(TestInfoFormStyle) as typeof TestInfoFormStyle
